import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EstabelecimentoDataService,
  EstabelecimentoService,
  TipoServicoService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  DeviceService,
  FormFieldComponent,
  IEstabelecimento,
} from '@nomoh-monorepo/shared-common-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

/**
 * Etapa 2: Categorias de Serviço
 * Usuário adiciona categorias de serviços (ex: Cabelo, Unhas, Estética)
 */
@Component({
  selector: 'lib-etapa-tipos-servico',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    ChipModule,
    MessageModule,
    FormFieldComponent,
    TranslateModule,
  ],
  templateUrl: './etapa-tipos-servico.component.html',
  styleUrl: './etapa-tipos-servico.component.scss',
})
export class EtapaTiposServicoComponent implements AfterViewInit {
  @ViewChild('headerTemplate')
  private headerTemplateRef?: TemplateRef<unknown>;
  @ViewChild('footerTemplate')
  private footerTemplateRef?: TemplateRef<unknown>;

  @Output() proximaEtapa = new EventEmitter<void>();
  @Output() fechar = new EventEmitter<void>();
  @Output() headerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();

  private fb = inject(FormBuilder);
  private tipoServicoService = inject(TipoServicoService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private estabelecimentoService = inject(EstabelecimentoService);
  private notificacaoService = inject(NotificacaoService);
  private translateService = inject(TranslateService);
  readonly deviceService = inject(DeviceService);

  formTipoServico: FormGroup;
  tiposServicosSignal = signal<string[]>([]);
  tiposServicosCadastradosSignal = signal<string[]>([]);
  salvando = false;
  estabelecimento: IEstabelecimento = {} as IEstabelecimento;

  constructor() {
    this.formTipoServico = this.fb.group({
      novoTipo: ['', Validators.required],
    });

    this.preencherFormulario();
  }

  ngAfterViewInit(): void {
    if (this.headerTemplateRef) {
      this.headerTemplateAction.emit(this.headerTemplateRef);
    }
    if (this.footerTemplateRef) {
      this.footerTemplateAction.emit(this.footerTemplateRef);
    }
  }

  private async preencherFormulario(): Promise<void> {
    this.salvando = true;
    this.estabelecimento = await this.estabelecimentoService.findCompleto() || {} as IEstabelecimento;
    if (this.estabelecimento?.tiposServico) {
      this.tiposServicosSignal.update((tipos) => [...tipos, ...(this.estabelecimento.tiposServico?.map((t) => t.nome) || [])]);
      this.tiposServicosCadastradosSignal.set(this.estabelecimento.tiposServico?.map((t) => t.nome) || []);
    }
    this.salvando = false;
  }

  adicionarTipo(): void {
    const rawValue = this.formTipoServico.get('novoTipo')?.value?.trim();

    if (!rawValue) {
      return;
    }

    const novosTipos = rawValue
      .split(',')
      .map((tipo: string) => tipo.trim())
      .filter((tipo: string) => tipo.length > 0);

    if (novosTipos.length === 0) {
      return;
    }

    const tiposAtuais = this.tiposServicosSignal();
    const paraAdicionar: string[] = [];
    const duplicados: string[] = [];

    for (const tipo of novosTipos) {
      if (tiposAtuais.includes(tipo) || paraAdicionar.includes(tipo)) {
        duplicados.push(tipo);
      } else {
        paraAdicionar.push(tipo);
      }
    }

    if (paraAdicionar.length === 0) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('PRIMEIRO_ACESSO.TIPOS_SERVICO.NOTIFICACAO_DUPLICADOS')
      );
      return;
    }

    this.tiposServicosSignal.update((tipos) => [...tipos, ...paraAdicionar]);

    if (duplicados.length > 0) {
      this.notificacaoService.notificacaoSuccess(
        this.translateService.instant('PRIMEIRO_ACESSO.TIPOS_SERVICO.NOTIFICACAO_ADICIONADOS_IGNORADOS', {
          adicionados: paraAdicionar.join(', '),
          duplicados: duplicados.join(', '),
        })
      );
    }

    this.formTipoServico.get('novoTipo')?.setValue('');
  }

  removerTipo(tipo: string): void {
    if (this.tiposServicosCadastradosSignal().includes(tipo)) {
      const idTipo = this.estabelecimento?.tiposServico?.find((t) => t.nome === tipo)?.id;
      this.tipoServicoService.deletar(idTipo || '');
    }
    this.tiposServicosSignal.update((tipos) => tipos.filter((t) => t !== tipo));
  }

  onEnter(event: Event): void {
    event.preventDefault();
    this.adicionarTipo();
  }

  onBlur(): void {
    // Auto-adicionar ao sair do campo (se tiver valor)
    const valor = this.formTipoServico.get('novoTipo')?.value?.trim();
    if (valor) {
      this.adicionarTipo();
    }
  }

  private async obterEstabelecimentoId(): Promise<string | undefined> {
    const idEmMemoria =
      this.estabelecimentoDataService.$estabelecimentoAtual()?.id ??
      this.estabelecimento?.id;

    if (idEmMemoria) {
      return idEmMemoria;
    }

    const estabelecimento = await this.estabelecimentoService.findCompleto();
    if (estabelecimento) {
      this.estabelecimento = estabelecimento;
      return estabelecimento.id;
    }

    return;
  }

  async salvar(): Promise<void> {
    if (this.tiposServicosSignal().length === 0) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('PRIMEIRO_ACESSO.TIPOS_SERVICO.NOTIFICACAO_MINIMO')
      );
      return;
    }

    if (this.tiposServicosCadastradosSignal().length) {
      this.tiposServicosSignal.set(this.tiposServicosSignal().filter((tipo) => !this.tiposServicosCadastradosSignal().includes(tipo)));
    }

    const estabelecimentoId = await this.obterEstabelecimentoId();

    try {
      this.salvando = true;

      const tipos = this.tiposServicosSignal();

      if (tipos.length > 0) {
        if (estabelecimentoId) {
          await this.tipoServicoService.criarMultiplo(
            tipos.map((nome) => ({ nome, estabelecimentoId }))
          );
        } else {
          console.error(
            'Estabelecimento não encontrado após recarregar dados'
          );
        }
      }

      this.proximaEtapa.emit();
    } catch (error) {
      console.error('Erro ao salvar categorias de serviço:', error);
      this.notificacaoService.notificacaoError(
        this.translateService.instant('PRIMEIRO_ACESSO.TIPOS_SERVICO.NOTIFICACAO_ERRO')
      );
    } finally {
      this.salvando = false;
    }
  }

  get temTipos(): boolean {
    return this.tiposServicosSignal().length > 0;
  }
}
